import type { AssistantTool, AssistantToolResult } from '@rammetec/app-kit/assistant'
import {
  buildEntitySchema,
  buildFilterableColumns,
  filterModelToAst,
} from '@rammetec/app-kit/query'
import type { EntityDescriptor, FieldDescriptor } from '@rammetec/data-access'
import { repositoryResolver } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import { getDescriptorForRule } from '@syscolect/domain'
import { buildFilterModelFromFlat } from '@/shared/crud/assistantFilterInput'
import { coerceFormFieldValue } from '@/shared/import/engine'

export { coerceFormFieldValue }

/** Scalar types the assistant may write via `form.setField`. */
const WRITABLE_TYPES = new Set<FieldDescriptor['type']>([
  'string',
  'number',
  'boolean',
  'date',
  'enum',
])

const SKIP_NAMES = new Set([
  'id',
  'organizationId',
  'version',
  'active',
  'inclusionDate',
  'lastModifiedDate',
  'inclusionUserId',
  'lastModifiedUserId',
])

/** Max candidates returned by `form.findLookupOptions` (hard cap). */
export const LOOKUP_CANDIDATE_CAP = 8

export interface WritableFormField {
  name: string
  label: string
  descriptor: FieldDescriptor
}

/** FK field present in the open form — resolved via repository, never via UI options. */
export interface RelationFormField {
  name: string
  label: string
  targetEntity: string
  displayField: string
}

export interface LookupCandidate {
  id: string
  label: string
}

/** Per-field cache of the last `form.findLookupOptions` result (keyed by field label). */
export type LookupCandidateCache = Map<string, readonly LookupCandidate[]>

export interface AssistantFormSnapshot {
  isNew: boolean
  recordLabel: string
  values: Record<string, unknown>
  writable: readonly WritableFormField[]
  /** FK fields present in the form (lookup via `form.findLookupOptions`). */
  relations: readonly RelationFormField[]
  /** Labels of `relations` — same order. */
  relationLabels: readonly string[]
  /**
   * Descriptor field with `autoNumber: true` when present on the form.
   * Never writable by the assistant — sequence allocates on save/create.
   */
  autoNumberField?: FieldDescriptor
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/** Resolve descriptor fields that exist in the form and are safe to set. */
export function resolveWritableFormFields(
  entity: string,
  formValues: Record<string, unknown>,
): {
  writable: WritableFormField[]
  relations: RelationFormField[]
  relationLabels: string[]
  autoNumberField?: FieldDescriptor
  descriptor: EntityDescriptor | undefined
} {
  const descriptor = getDescriptorForRule(entity, 'assistant-form')
  if (!descriptor) {
    return { writable: [], relations: [], relationLabels: [], descriptor: undefined }
  }

  const formKeys = new Set(Object.keys(formValues))
  const fkByName = new Map((descriptor.foreignKeys ?? []).map((fk) => [fk.fieldName, fk]))
  const relations: RelationFormField[] = []
  const writable: WritableFormField[] = []
  const seenLabels = new Set<string>()
  let autoNumberField: FieldDescriptor | undefined

  for (const field of descriptor.fields) {
    if (!formKeys.has(field.name)) continue
    if (SKIP_NAMES.has(field.name) || field.virtual) continue
    // Sequence allocates on save — same cut as resolveCreatableFields (assistantCreateRecord).
    if (field.autoNumber === true) {
      autoNumberField = field
      continue
    }

    const fk = fkByName.get(field.name)
    if (fk) {
      if (seenLabels.has(field.label)) continue
      seenLabels.add(field.label)
      relations.push({
        name: field.name,
        label: field.label,
        targetEntity: fk.targetEntity,
        displayField: fk.displayField ?? 'description',
      })
      continue
    }

    if (!WRITABLE_TYPES.has(field.type)) continue
    if (seenLabels.has(field.label)) continue
    seenLabels.add(field.label)
    writable.push({ name: field.name, label: field.label, descriptor: field })
  }

  return {
    writable,
    relations,
    relationLabels: relations.map((r) => r.label),
    ...(autoNumberField ? { autoNumberField } : {}),
    descriptor,
  }
}

function formatEnumAccepted(field: FieldDescriptor): string {
  if (!field.enumValues?.length) return ''
  const labels = field.enumLabels
  const parts = field.enumValues.map((v) => {
    const label = labels?.[v]
    return label && label !== v ? `${v}=${label}` : v
  })
  return parts.join(', ')
}

function suggestLabels(all: readonly string[], query: string, cap = 5): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return all.slice(0, cap)
  const scored = all
    .map((label) => {
      const l = label.toLowerCase()
      let score = 0
      if (l === q) score = 3
      else if (l.startsWith(q)) score = 2
      else if (l.includes(q)) score = 1
      return { label, score }
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
  return scored.slice(0, cap).map((x) => x.label)
}

export interface SetFieldItem {
  campo: string
  valor: string
}

export interface SetFieldParams {
  campos: SetFieldItem[]
}

export interface FindLookupParams {
  campo: string
  busca: string
}

function formatCandidateLine(c: LookupCandidate): string {
  return `${c.label} (id=${c.id})`
}

function candidateLabelFromRow(
  row: Record<string, unknown>,
  displayField: string,
): string {
  const raw = row[displayField]
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
  const id = row.id
  if (typeof id === 'string' && id.trim()) return id.trim()
  return '—'
}

/**
 * Search target entity for lookup candidates (request 0294).
 * Same repositoryResolver + contains path as data.findRecords (0293).
 */
export async function searchLookupCandidates(
  targetEntity: string,
  displayField: string,
  busca: string,
): Promise<
  | { ok: true; candidates: LookupCandidate[]; truncated: boolean }
  | { ok: false; error: string }
> {
  let schema: ReturnType<typeof buildEntitySchema>
  let columns: ReturnType<typeof buildFilterableColumns>
  try {
    schema = buildEntitySchema(targetEntity, { maxDepth: 0 })
    columns = buildFilterableColumns(schema).filter((c) => c.depth === 0)
  } catch (error) {
    rtLogService.warn('[assistant] form.findLookupOptions schema failed', {
      targetEntity,
      error,
    })
    return {
      ok: false,
      error: `esquema indisponível para buscar em ${targetEntity}; tente de novo`,
    }
  }

  const built = buildFilterModelFromFlat(
    {
      combinator: 'and',
      conditions: [{ field: displayField, operator: 'contains', value: busca }],
      groups: [],
    },
    columns,
  )
  if (!built.ok) return built

  const { ast, ignoredCount } = filterModelToAst(built.model, schema)
  if (ignoredCount > 0) {
    return {
      ok: false,
      error: `campo de exibição "${displayField}" não é filtrável nesta entidade`,
    }
  }

  try {
    const repo = await repositoryResolver.resolve<Record<string, unknown> & { id: string }>(
      targetEntity,
    )
    const page = await repo.getPage({
      limit: LOOKUP_CANDIDATE_CAP,
      offset: 0,
      filterAst: ast,
    })
    const candidates: LookupCandidate[] = []
    for (const item of page.items) {
      const row = item as Record<string, unknown>
      const id = row.id
      if (typeof id !== 'string' || !id.trim()) continue
      candidates.push({ id, label: candidateLabelFromRow(row, displayField) })
    }
    return {
      ok: true,
      candidates,
      truncated: page.hasMore || candidates.length >= LOOKUP_CANDIDATE_CAP,
    }
  } catch (error) {
    rtLogService.warn('[assistant] form.findLookupOptions failed', {
      targetEntity,
      error,
    })
    return {
      ok: false,
      error: `Não consegui buscar opções. Tente de novo.`,
    }
  }
}

function buildConfirmSummary(
  params: SetFieldParams,
  lookupCache: LookupCandidateCache,
  relationLabelSet: ReadonlySet<string>,
): string {
  const displayValor = (campo: string, valor: string): string => {
    if (!relationLabelSet.has(campo)) return valor
    const hit = lookupCache.get(campo)?.find((c) => c.id === valor)
    return hit?.label ?? valor
  }

  const items = params.campos
  if (items.length > 3) return `Preencher ${items.length} campos`
  if (items.length === 1) {
    const only = items[0]!
    return `Preencher ${only.campo} com ${displayValor(only.campo, only.valor)}`
  }
  const parts = items.map((i) => `${i.campo} com ${displayValor(i.campo, i.valor)}`)
  const last = parts.pop()!
  return `Preencher ${parts.join(', ')} e ${last}`
}

/**
 * Form tools for the in-app assistant (requests 0292 / 0294 — spec 0012 phases E+G).
 * `getSnapshot` / `setField` / `lookupCache` must be live — do not close over render values.
 */
export function createAssistantFormTools(
  getSnapshot: () => AssistantFormSnapshot,
  setField: (name: string, value: unknown) => void,
  lookupCache: LookupCandidateCache,
): AssistantTool[] {
  const snap = getSnapshot()
  const scalarLabels = snap.writable.map((f) => f.label)
  const relationLabels = snap.relations.map((r) => r.label)
  const setFieldLabelEnum = [...scalarLabels, ...relationLabels]

  const findLookupTool: AssistantTool<FindLookupParams> = {
    name: 'form.findLookupOptions',
    description:
      'Busca candidatos para um campo de relação (lookup) do formulário aberto. ' +
      'Não preenche — depois use form.setField com o id de um candidato desta busca. ' +
      `campo = rótulo do campo de relação. busca = texto livre (contains). Até ${LOOKUP_CANDIDATE_CAP} resultados.`,
    parameters: {
      type: 'object',
      properties: {
        campo: {
          type: 'string',
          description: 'Rótulo do campo de relação no formulário.',
          ...(relationLabels.length > 0 ? { enum: relationLabels } : {}),
        },
        busca: {
          type: 'string',
          description: 'Trecho do nome/rótulo do registro alvo.',
        },
      },
      required: ['campo', 'busca'],
      additionalProperties: false,
    },
    kind: 'read',
    confirm: 'never',
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      if (typeof raw.campo !== 'string' || !raw.campo.trim()) {
        return { ok: false, error: 'não entendi: informe o campo (rótulo de relação)' }
      }
      if (typeof raw.busca !== 'string') {
        return { ok: false, error: 'não entendi: busca deve ser texto' }
      }
      const campo = raw.campo.trim()
      const busca = raw.busca.trim()
      if (!busca) {
        return { ok: false, error: 'não entendi: informe um termo em busca' }
      }

      const { relations } = getSnapshot()
      const labels = relations.map((r) => r.label)
      if (!labels.includes(campo)) {
        const suggested = suggestLabels(labels, campo)
        const hint =
          suggested.length > 0
            ? `Campos de relação próximos: ${suggested.join(', ')}`
            : labels.length > 0
              ? `Campos de relação: ${labels.slice(0, 8).join(', ')}`
              : 'Nenhum campo de relação neste formulário'
        return { ok: false, error: `não entendi: campo "${campo}". ${hint}` }
      }

      return { ok: true, params: { campo, busca } }
    },
    confirmSummary: (p) => `Buscar opções de ${p.campo} com "${p.busca}"`,
    run: async (params): Promise<AssistantToolResult> => {
      const { relations } = getSnapshot()
      const field = relations.find((r) => r.label === params.campo)
      if (!field) {
        return { ok: false, message: `não entendi: campo "${params.campo}"` }
      }

      const result = await searchLookupCandidates(
        field.targetEntity,
        field.displayField,
        params.busca,
      )
      if (!result.ok) {
        return { ok: false, message: result.error }
      }

      lookupCache.set(params.campo, result.candidates)
      const n = result.candidates.length

      if (n === 0) {
        return {
          ok: true,
          message: `Nenhum registro casou com "${params.busca}" em ${params.campo}. Tente outro termo.`,
          data: { total: 0, returned: 0, truncated: false },
        }
      }

      const list = result.candidates.map(formatCandidateLine).join('; ')
      const truncNote = result.truncated
        ? ` (mostrando até ${LOOKUP_CANDIDATE_CAP}; refine a busca se precisar)`
        : ''
      return {
        ok: true,
        message:
          n === 1
            ? `1 opção para ${params.campo}: ${list}. Use form.setField com o id.`
            : `${n} opções para ${params.campo}${truncNote}: ${list}. Use form.setField com o id escolhido.`,
        data: {
          total: n,
          returned: n,
          truncated: result.truncated,
        },
      }
    },
  }

  const setFieldTool: AssistantTool<SetFieldParams> = {
    name: 'form.setField',
    label: 'Preencher campo',
    description:
      'Preenche um ou mais campos do formulário aberto. Não salva — o usuário confirma com Salvar. ' +
      'campo usa o rótulo exibido na tela. valor é sempre texto (número, data e booleano são convertidos). ' +
      'Campo de relação: valor = id de um candidato de form.findLookupOptions daquele campo (busque antes). ' +
      'Campo autoNumber (ex.: Código): preenchido pelo sistema — não informe.',
    parameters: {
      type: 'object',
      properties: {
        campos: {
          type: 'array',
          minItems: 1,
          items: {
            type: 'object',
            properties: {
              campo: {
                type: 'string',
                description: 'Rótulo do campo no formulário.',
                ...(setFieldLabelEnum.length > 0 ? { enum: setFieldLabelEnum } : {}),
              },
              valor: {
                type: 'string',
                description:
                  'Valor em texto; em campo de relação, o id retornado por form.findLookupOptions.',
              },
            },
            required: ['campo', 'valor'],
            additionalProperties: false,
          },
        },
      },
      required: ['campos'],
      additionalProperties: false,
    },
    kind: 'write',
    // Screen-only (user still saves) — eligible for "Sempre aplicar".
    rememberable: true,
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      if (!Array.isArray(raw.campos) || raw.campos.length === 0) {
        return { ok: false, error: 'não entendi: informe ao menos um item em campos' }
      }

      const { writable, relations, autoNumberField } = getSnapshot()
      const fieldByLabel = new Map(writable.map((f) => [f.label, f]))
      const relationByLabel = new Map(relations.map((r) => [r.label, r]))
      const allLabels = [...writable.map((f) => f.label), ...relations.map((r) => r.label)]

      const parsed: SetFieldItem[] = []

      for (let i = 0; i < raw.campos.length; i++) {
        const entry = raw.campos[i]
        if (!isRecord(entry)) {
          return { ok: false, error: `não entendi: item ${i + 1} de campos é inválido` }
        }
        if (typeof entry.campo !== 'string' || !entry.campo.trim()) {
          return { ok: false, error: `não entendi: item ${i + 1} precisa de campo (rótulo)` }
        }
        if (typeof entry.valor !== 'string') {
          return {
            ok: false,
            error: `não entendi: valor de "${entry.campo}" deve ser texto`,
          }
        }

        const campo = entry.campo.trim()
        const valor = entry.valor

        if (autoNumberField && autoNumberField.label === campo) {
          return {
            ok: false,
            error:
              `"${campo}" é numeração automática — o sistema preenche na gravação. ` +
              `Não informe; preencha os demais obrigatórios.`,
          }
        }

        if (relationByLabel.has(campo)) {
          const candidates = lookupCache.get(campo)
          if (!candidates?.length) {
            return {
              ok: false,
              error: `use form.findLookupOptions para "${campo}" antes de preencher`,
            }
          }
          const hit = candidates.find((c) => c.id === valor)
          if (!hit) {
            return {
              ok: false,
              error:
                `id "${valor}" não está entre as opções de "${campo}". ` +
                `Busque de novo com form.findLookupOptions.`,
            }
          }
          parsed.push({ campo, valor })
          continue
        }

        const field = fieldByLabel.get(campo)
        if (!field) {
          const suggested = suggestLabels(allLabels, campo)
          const hint =
            suggested.length > 0
              ? `Campos próximos: ${suggested.join(', ')}`
              : allLabels.length > 0
                ? `Campos disponíveis: ${allLabels.slice(0, 8).join(', ')}`
                : 'Nenhum campo preenchível neste formulário'
          return { ok: false, error: `não entendi: campo "${campo}". ${hint}` }
        }

        const coerced = coerceFormFieldValue(field.descriptor, valor)
        if (!coerced.ok) return { ok: false, error: coerced.error }

        parsed.push({ campo, valor })
      }

      return { ok: true, params: { campos: parsed } }
    },
    confirmSummary: (params) => {
      const { relations } = getSnapshot()
      return buildConfirmSummary(
        params,
        lookupCache,
        new Set(relations.map((r) => r.label)),
      )
    },
    run: (params) => {
      const { writable, relations } = getSnapshot()
      const fieldByLabel = new Map(writable.map((f) => [f.label, f]))
      const relationByLabel = new Map(relations.map((r) => [r.label, r]))

      // All-or-nothing: validate again then apply.
      const pending: { name: string; value: unknown; label: string }[] = []
      for (const item of params.campos) {
        const relation = relationByLabel.get(item.campo)
        if (relation) {
          const candidates = lookupCache.get(item.campo)
          if (!candidates?.length) {
            return {
              ok: false,
              message: `use form.findLookupOptions para "${item.campo}" antes de preencher`,
            }
          }
          const hit = candidates.find((c) => c.id === item.valor)
          if (!hit) {
            return {
              ok: false,
              message:
                `id "${item.valor}" não está entre as opções de "${item.campo}". ` +
                `Busque de novo com form.findLookupOptions.`,
            }
          }
          pending.push({ name: relation.name, value: hit.id, label: relation.label })
          continue
        }

        const field = fieldByLabel.get(item.campo)
        if (!field) {
          return {
            ok: false,
            message: `não entendi: campo "${item.campo}"`,
          }
        }
        const coerced = coerceFormFieldValue(field.descriptor, item.valor)
        if (!coerced.ok) {
          return { ok: false, message: coerced.error }
        }
        pending.push({ name: field.name, value: coerced.value, label: field.label })
      }

      for (const p of pending) {
        setField(p.name, p.value)
      }

      const n = pending.length
      return {
        ok: true,
        message:
          n === 1
            ? `Campo "${pending[0]!.label}" preenchido (não salvo).`
            : `${n} campos preenchidos (não salvos).`,
        data: { filled: n },
      }
    },
  }

  const tools: AssistantTool[] = [setFieldTool]
  if (relationLabels.length > 0) {
    tools.unshift(findLookupTool)
  }
  return tools
}

function pickRecordLabel(values: Record<string, unknown>): string {
  for (const key of ['displayName', 'description', 'reference', 'name', 'code']) {
    const v = values[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  }
  return ''
}

/** One-line pt-BR summary for the dialog form scope. */
export function buildAssistantFormSummary(snap: AssistantFormSnapshot): string {
  const title = snap.isNew
    ? 'Novo registro'
    : snap.recordLabel
      ? `Editando: ${snap.recordLabel}`
      : 'Editando registro'

  const enumParts: string[] = []
  for (const f of snap.writable) {
    if (f.descriptor.type !== 'enum' || !f.descriptor.enumValues?.length) continue
    enumParts.push(`${f.label}: ${formatEnumAccepted(f.descriptor)}`)
  }

  // autoNumber is never in writable — must not appear as "obrigatório vazio".
  const missingRequired = snap.writable
    .filter((f) => f.descriptor.required && isEmptyValue(snap.values[f.name]))
    .map((f) => f.label)

  const parts = [title]
  if (snap.isNew && snap.autoNumberField) {
    parts.push(`${snap.autoNumberField.label} preenchido pelo sistema`)
  }
  if (enumParts.length > 0) {
    parts.push(`Enums: ${enumParts.join('; ')}`)
  }
  if (missingRequired.length > 0) {
    const list =
      missingRequired.length <= 6
        ? missingRequired.join(', ')
        : `${missingRequired.slice(0, 5).join(', ')}…`
    parts.push(`Obrigatórios vazios: ${list}`)
  }
  return parts.join('. ')
}

export function buildAssistantFormExamples(
  writable: readonly WritableFormField[],
  relations: readonly RelationFormField[] = [],
): string[] {
  const examples: string[] = []
  const by = (re: RegExp) => writable.find((f) => re.test(f.label))

  const unit = by(/unidade/i)
  const qty = by(/quantidade/i)
  const desc = by(/descri/i)
  const name = by(/^nome/i)
  const price = by(/valor|preço|preco/i)
  const ref = by(/referên|referen/i)

  if (unit && qty) {
    examples.push(`põe a ${qty.label.toLowerCase()} em 250 e a ${unit.label.toLowerCase()} em kg`)
  } else if (unit) {
    examples.push(`põe a ${unit.label.toLowerCase()} em kg`)
  }
  if (desc) {
    examples.push(`preenche a ${desc.label.toLowerCase()} com tecido azul`)
  } else if (name) {
    examples.push(`preenche o ${name.label.toLowerCase()} com Silva Comércio`)
  }
  if (price) {
    examples.push(`define o ${price.label.toLowerCase()} como 19,90`)
  } else if (ref) {
    examples.push(`muda a ${ref.label.toLowerCase()} para ABC-100`)
  }

  if (relations.length > 0 && examples.length < 3) {
    const rel =
      relations.find((r) => /fornecedor/i.test(r.label)) ??
      relations.find((r) => /grupo|marca/i.test(r.label)) ??
      relations[0]!
    examples.push(`põe o ${rel.label.toLowerCase()} Silva`)
  }

  if (examples.length === 0 && writable.length > 0) {
    const first = writable[0]!
    examples.push(`preenche ${first.label.toLowerCase()}`)
  }

  return examples.slice(0, 3)
}

/** Build a live snapshot from form values + entity name. */
export function snapshotAssistantForm(
  entity: string,
  formValues: Record<string, unknown>,
): AssistantFormSnapshot {
  const { writable, relations, relationLabels, autoNumberField } = resolveWritableFormFields(
    entity,
    formValues,
  )
  const id = formValues.id
  const isNew = id == null || id === ''
  return {
    isNew,
    recordLabel: pickRecordLabel(formValues),
    values: formValues,
    writable,
    relations,
    relationLabels,
    ...(autoNumberField ? { autoNumberField } : {}),
  }
}
